import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Highlight, themes } from 'prism-react-renderer';
import type { Language } from 'prism-react-renderer';

interface CodeBlockProps {
  children: string;
  className?: string;
}

interface Token {
  types: string[];
  content: string;
  empty?: boolean;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ children, className }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Extract language from className (e.g., "language-javascript" -> "javascript")
  const language = (className?.replace('language-', '') || 'text') as Language;

  return (
    <div className="relative group mt-2 mb-2">
      <div className="absolute right-2 top-2 z-10">
        <button
          onClick={handleCopy}
          className="p-1.5 bg-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-600"
          aria-label="Copy code"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
          )}
        </button>
      </div>
      <Highlight
        theme={themes.vsDark}
        code={children.trim()}
        language={language}
      >
        {({ className: highlightClassName, style, tokens, getLineProps, getTokenProps }) => (
          <div className="bg-gray-900 rounded-lg overflow-hidden">
            {language !== 'text' && (
              <div className="px-4 py-2 bg-gray-800 border-b border-gray-700">
                <span className="text-sm text-gray-500">{language}</span>
              </div>
            )}
            <pre className={`${highlightClassName} p-4 overflow-x-auto`} style={style}>
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line, key: i })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </div>
              ))}
            </pre>
          </div>
        )}
      </Highlight>
    </div>
  );
};